import { QualCSLevelOrder, QualRxLevelOrder, QualProviderTypeOrder } from '#modules/qualifications'
import type { QualCategory, QualProviderType, Qualification } from '#modules/qualifications'

export type WithQuals = {
  employee_qualifications?: { qualifications: Qualification }[] | null
}

// -- Category predicates ------------------------------------------------------

/** Returns true if the category is a medical category (clinical or EMS). */
function isMedicalCategory(category: QualCategory): boolean {
  return category === 'clinical' || category === 'ems'
}

// -- Internal helpers ---------------------------------------------------------

function maxCSLevel(emp: WithQuals): number {
  return emp.employee_qualifications?.reduce(
    (max, eq) => Math.max(max, QualCSLevelOrder[eq.qualifications.cs_level] ?? 0), 0
  ) ?? 0
}

function maxRxLevel(emp: WithQuals): number {
  return emp.employee_qualifications?.reduce(
    (max, eq) => Math.max(max, QualRxLevelOrder[eq.qualifications.rx_level] ?? 0), 0
  ) ?? 0
}

// -- Qualification display helpers --------------------------------------------

/** Returns a Set of all qualification abbreviations, ordered by sort_order. */
export function getQualAbbrevs(emp: WithQuals): Set<string> {
  return new Set(
    (emp.employee_qualifications ?? [])
      .slice()
      .sort((a, b) => a.qualifications.sort_order - b.qualifications.sort_order)
      .map(eq => eq.qualifications.abbreviation)
  )
}

/** Returns a comma-separated string of all qualification abbreviations. */
export function formatQuals(emp: WithQuals | null | undefined): string {
  if (!emp) return ''
  return [...getQualAbbrevs(emp)].join(', ')
}

/** Returns the abbreviation of the primary medical qualification (highest rx_level, then cs_level, then sort_order). */
export function primaryMedicalQual(emp: WithQuals): string {
  return (emp.employee_qualifications ?? [])
    .filter(eq => isMedicalCategory(eq.qualifications.category))
    .sort((a, b) => {
      const rxDiff = QualRxLevelOrder[b.qualifications.rx_level] - QualRxLevelOrder[a.qualifications.rx_level]
      if (rxDiff !== 0) return rxDiff
      const csDiff = QualCSLevelOrder[b.qualifications.cs_level] - QualCSLevelOrder[a.qualifications.cs_level]
      if (csDiff !== 0) return csDiff
      return a.qualifications.sort_order - b.qualifications.sort_order
    })[0]?.qualifications.abbreviation ?? ''
}

/** Returns a comma-separated string of medical qualification abbreviations only.
 *  Use in medical contexts: MAR, CS, encounter provider dropdowns. */
export function formatMedicalQuals(emp: WithQuals): string {
  return (emp.employee_qualifications ?? [])
    .filter(eq => isMedicalCategory(eq.qualifications.category))
    .map(eq => eq.qualifications.abbreviation)
    .join(', ')
}

// -- CS authority -------------------------------------------------------------

/** Returns true if the employee can witness CS wastage or co-signatures (EMT and above). */
export function canWitnessCS(emp: WithQuals): boolean {
  return maxCSLevel(emp) >= QualCSLevelOrder.witness
}

/** Returns true if the employee can dispense/administer controlled substances (PMD, RN and above). */
export function canDispenseCS(emp: WithQuals): boolean {
  return maxCSLevel(emp) >= QualCSLevelOrder.dispenser
}

// -- Rx authority -------------------------------------------------------------

/** Returns true if the employee can administer prescription medications (RN and above). */
export function canAdministerRx(emp: WithQuals): boolean {
  return maxRxLevel(emp) >= QualRxLevelOrder.dispenser
}

/** Returns true if the employee can prescribe/authorize medications (NP, PA, MD, DO). */
export function canPrescribe(emp: WithQuals): boolean {
  return maxRxLevel(emp) >= QualRxLevelOrder.prescriber
}

// -- Medical umbrella ---------------------------------------------------------

/** Returns true if the employee holds any medical qualification (clinical or EMS). */
export function isMedical(emp: WithQuals): boolean {
  return emp.employee_qualifications?.some(eq => eq.qualifications.provider_type !== 'none') ?? false
}

export function hasProviderType(emp: WithQuals, type: QualProviderType): boolean {
  return emp.employee_qualifications?.some(eq => eq.qualifications.provider_type === type) ?? false
}

/** Returns the employee's primary (highest-priority) provider type. */
export function primaryProviderType(emp: WithQuals): QualProviderType {
  return (emp.employee_qualifications ?? [])
    .map(eq => eq.qualifications.provider_type)
    .reduce((best, t) => (QualProviderTypeOrder[t] > QualProviderTypeOrder[best] ? t : best), 'none')
}

// -- Qual predicates ----------------------------------------------------------

/** Returns true if the employee holds a specific qualification by abbreviation. */
export function hasQual(emp: WithQuals, abbreviation: string): boolean {
  return emp.employee_qualifications?.some(eq => eq.qualifications.abbreviation === abbreviation) ?? false
}

/** Returns the ICS position label for an employee based on unit deployment context.
 *  deploysOnFireline=true  -> fireline quals (ENGB > CRWB > FFT1 > FFT2)
 *  deploysOnFireline=false -> medical quals (highest prescribing authority first)
 *  Returns empty string if no matching quals. */
export function primaryIcsPosition(emp: WithQuals, deploysOnFireline: boolean): string {
  const quals = (emp.employee_qualifications ?? []).map(eq => eq.qualifications)
  if (!quals.length) return ''
  if (deploysOnFireline) {
    return quals
      .filter(q => q.category === 'fireline')
      .sort((a, b) => a.sort_order - b.sort_order)[0]?.ics_position ?? ''
  } else {
    return quals
      .filter(q => isMedicalCategory(q.category))
      .sort((a, b) => {
        const rxDiff = QualRxLevelOrder[b.rx_level] - QualRxLevelOrder[a.rx_level]
        if (rxDiff !== 0) return rxDiff
        const csDiff = QualCSLevelOrder[b.cs_level] - QualCSLevelOrder[a.cs_level]
        if (csDiff !== 0) return csDiff
        return a.sort_order - b.sort_order
      })[0]?.ics_position ?? ''
  }
}
